import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Upload, Search, Trash2, Loader2, BookOpen, FileText,
  RefreshCw, Sparkles, X, Tag,
} from "lucide-react";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dynamic Spiritual Library — upload any PDF, auto-index into MongoDB
 * and search it locally. Zero AI credits per search or browse.
 */
export default function LibraryPage() {
  const [docs, setDocs] = useState([]);
  const [stats, setStats] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [tags, setTags] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [scopeDocId, setScopeDocId] = useState(null);
  const [reindexing, setReindexing] = useState({});
  const [deleting, setDeleting] = useState({});
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [d, s] = await Promise.all([
        axios.get(`${API}/library/documents`),
        axios.get(`${API}/library/stats`),
      ]);
      setDocs(d.data.documents || []);
      setStats(s.data);
    } catch (e) { toast.error("Failed to load library"); }
  };

  useEffect(() => { load(); }, []);

  // debounced search
  useEffect(() => {
    if (!query || query.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const params = { q: query, limit: 30 };
        if (scopeDocId) params.doc_id = scopeDocId;
        const { data } = await axios.get(`${API}/library/search`, { params });
        setResults(data);
      } catch (e) { toast.error("Search failed"); }
      setSearching(false);
    }, 250);
    return () => clearTimeout(t);
  }, [query, scopeDocId]);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files are supported"); return;
    }
    setUploading(true); setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("tags", tags);
      await axios.post(`${API}/library/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round(100 * evt.loaded / evt.total));
        },
        timeout: 5 * 60 * 1000,
      });
      toast.success(`Indexed: ${f.name}`);
      setTags("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false); setUploadProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const del = async (doc) => {
    if (!confirm(`Delete "${doc.filename}" and all its indexed chunks?`)) return;
    setDeleting((d) => ({ ...d, [doc.doc_id]: true }));
    try {
      await axios.delete(`${API}/library/documents/${doc.doc_id}`);
      toast.success("Deleted");
      if (scopeDocId === doc.doc_id) setScopeDocId(null);
      await load();
    } catch (e) { toast.error("Delete failed"); }
    setDeleting((d) => ({ ...d, [doc.doc_id]: false }));
  };

  const reindex = async (doc) => {
    setReindexing((d) => ({ ...d, [doc.doc_id]: true }));
    try {
      await axios.post(`${API}/library/documents/${doc.doc_id}/reindex`);
      toast.success("Re-indexed");
      await load();
    } catch (e) { toast.error("Re-index failed"); }
    setReindexing((d) => ({ ...d, [doc.doc_id]: false }));
  };

  const openPdf = (doc) => {
    window.open(`${API}/library/documents/${doc.doc_id}/file`, "_blank");
  };

  const scopedDoc = useMemo(() => docs.find((d) => d.doc_id === scopeDocId), [docs, scopeDocId]);

  const fmtBytes = (n) => {
    if (!n) return "0";
    const u = ["B", "KB", "MB", "GB"];
    let i = 0; let v = n;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(1)} ${u[i]}`;
  };

  const highlight = (text, q) => {
    if (!q) return text;
    const parts = text.split(new RegExp(`(${q.split(/\s+/)[0].replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig"));
    return parts.map((p, i) => p.toLowerCase() === q.split(/\s+/)[0].toLowerCase()
      ? <mark key={i} className="bg-[#D4AF37]/40 text-[#E6E1D6] px-0.5 rounded-sm">{p}</mark>
      : p);
  };

  return (
    <div className="min-h-screen bg-[#0F1115] text-[#E6E1D6]">
      <div className="grain-overlay pointer-events-none fixed inset-0 z-0" />

      <header className="relative z-10 border-b border-[#2A2D35] bg-[#0F1115]/90 backdrop-blur-md sticky top-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/"
                  className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-mono
                             text-[#A19E95] hover:text-[#D4AF37] transition-colors"
                  data-testid="library-back-link">
              <ArrowLeft size={12} /> Dashboard
            </Link>
            <div className="h-6 w-px bg-[#2A2D35]" />
            <div className="min-w-0">
              <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.4em] text-[#6E6B64] font-mono">
                Zero-AI · Local search
              </div>
              <div className="font-heading text-lg sm:text-xl text-[#E6E1D6] flex items-center gap-2">
                <BookOpen size={18} className="text-[#D4AF37]" />
                Dynamic Spiritual Library
              </div>
            </div>
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Intro */}
        <div className="mb-6 border border-[#2A2D35] bg-[#151821] rounded-sm px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex items-start gap-3">
            <Sparkles className="text-[#D4AF37] shrink-0 mt-0.5" size={16} />
            <div className="text-xs sm:text-sm text-[#A19E95] leading-relaxed">
              Upload any spiritual, homeopathic or astrological PDF. Every page is extracted, chunked
              and indexed in your database. Search everything instantly — zero AI credits per search.
              {stats && (
                <span className="block mt-2 font-mono text-[#D4AF37]">
                  {stats.total_documents} document{stats.total_documents === 1 ? "" : "s"} ·{" "}
                  {stats.total_chunks} chunks · {fmtBytes(stats.total_size_bytes)} · {(stats.total_chars / 1000).toFixed(1)}k characters indexed
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* LEFT: upload + document list */}
          <div className="lg:col-span-1 space-y-4">
            {/* Upload */}
            <div className="border border-[#2A2D35] bg-[#151821] rounded-sm p-4 space-y-3">
              <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#6E6B64]">
                Add to library
              </div>
              <div>
                <Input value={tags}
                       onChange={(e) => setTags(e.target.value)}
                       placeholder="Optional tags (comma-separated)"
                       className="bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]"
                       data-testid="library-tags-input" />
              </div>
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-full h-28 border-2 border-dashed border-[#2A2D35] hover:border-[#D4AF37]/50
                           rounded-sm flex flex-col items-center justify-center gap-2 text-[#6E6B64]
                           hover:text-[#D4AF37] transition-colors disabled:opacity-50"
                data-testid="library-upload-dropzone">
                {uploading ? (
                  <>
                    <Loader2 size={22} className="animate-spin" />
                    <span className="text-xs">Uploading & indexing… {uploadProgress}%</span>
                    <span className="text-[10px] font-mono">This is a one-time cost</span>
                  </>
                ) : (
                  <>
                    <Upload size={22} />
                    <span className="text-xs">Click to upload a PDF</span>
                    <span className="text-[10px] font-mono">.pdf up to 50 MB</span>
                  </>
                )}
              </button>
              <input ref={fileRef} type="file" accept="application/pdf"
                     onChange={onPickFile} className="hidden"
                     data-testid="library-file-input" />
            </div>

            {/* Document list */}
            <div className="border border-[#2A2D35] bg-[#151821] rounded-sm p-4 space-y-2"
                 data-testid="library-doc-list">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#6E6B64]">
                  Your documents · {docs.length}
                </div>
                {scopeDocId && (
                  <button onClick={() => setScopeDocId(null)}
                          className="text-[10px] text-[#D4AF37] hover:underline"
                          data-testid="library-clear-scope">
                    Clear scope
                  </button>
                )}
              </div>
              {docs.length === 0 ? (
                <div className="text-xs text-[#6E6B64] italic py-4">
                  No PDFs yet — upload above to start building your library.
                </div>
              ) : docs.map((d) => {
                const active = scopeDocId === d.doc_id;
                return (
                  <div key={d.doc_id}
                       className={`border rounded-sm p-3 ${
                         active ? "border-[#D4AF37] bg-[#D4AF37]/5"
                                : "border-[#2A2D35] bg-[#0F1115]"
                       }`}
                       data-testid={`library-doc-${d.doc_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 text-sm text-[#E6E1D6] font-medium truncate">
                          <FileText size={12} className="text-[#D4AF37] shrink-0" />
                          {d.filename}
                        </div>
                        <div className="text-[10px] font-mono text-[#6E6B64] mt-0.5">
                          {d.page_count}p · {d.chunk_count} chunks · {fmtBytes(d.size_bytes)}
                        </div>
                        {d.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {d.tags.map((t) => (
                              <span key={t}
                                    className="text-[9px] px-1.5 py-0.5 rounded-sm border border-[#2A2D35] text-[#A19E95] flex items-center gap-1">
                                <Tag size={8} />{t}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <button onClick={() => del(d)}
                              disabled={deleting[d.doc_id]}
                              className="text-[#6E6B64] hover:text-rose-400 shrink-0"
                              data-testid={`library-delete-${d.doc_id}`}>
                        {deleting[d.doc_id] ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                      </button>
                    </div>
                    <div className="flex gap-1 mt-2">
                      <button onClick={() => setScopeDocId(active ? null : d.doc_id)}
                              className={`text-[10px] px-2 py-1 rounded-sm border transition-colors ${
                                active ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                                       : "border-[#2A2D35] text-[#A19E95] hover:text-[#E6E1D6]"
                              }`}
                              data-testid={`library-scope-${d.doc_id}`}>
                        {active ? "Scoped" : "Search here"}
                      </button>
                      <button onClick={() => openPdf(d)}
                              className="text-[10px] px-2 py-1 rounded-sm border border-[#2A2D35] text-[#A19E95] hover:text-[#E6E1D6]">
                        Open PDF
                      </button>
                      <button onClick={() => reindex(d)}
                              disabled={reindexing[d.doc_id]}
                              className="text-[10px] px-2 py-1 rounded-sm border border-[#2A2D35] text-[#A19E95] hover:text-[#E6E1D6] disabled:opacity-50 flex items-center gap-1"
                              data-testid={`library-reindex-${d.doc_id}`}>
                        {reindexing[d.doc_id] ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                        Re-index
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT: search + results */}
          <div className="lg:col-span-2 space-y-4">
            <div className="border border-[#2A2D35] bg-[#151821] rounded-sm p-4 space-y-3">
              <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#6E6B64]">
                Local full-text search
                {scopedDoc && <span className="ml-2 text-[#D4AF37]">scoped to {scopedDoc.filename}</span>}
              </div>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E6B64]" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search any word or phrase across all uploaded PDFs…"
                  className="pl-9 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]"
                  data-testid="library-search-input"
                />
                {query && (
                  <button onClick={() => { setQuery(""); setResults(null); }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6E6B64] hover:text-[#E6E1D6]">
                    <X size={14} />
                  </button>
                )}
              </div>
              {searching && <div className="text-xs text-[#6E6B64] font-mono">Searching…</div>}
            </div>

            {results && results.count === 0 && (
              <div className="border border-[#2A2D35] bg-[#151821] rounded-sm p-4 text-sm text-[#6E6B64] italic"
                   data-testid="library-empty-results">
                No matches for "{results.query}". Try a shorter or different keyword.
              </div>
            )}

            {results && results.count > 0 && (
              <div className="space-y-3" data-testid="library-search-results">
                <div className="text-[10px] uppercase tracking-widest font-mono text-[#6E6B64]">
                  {results.count} match{results.count === 1 ? "" : "es"} · zero AI credits used
                </div>
                {results.results.map((r) => (
                  <div key={r.chunk_id}
                       className="border border-[#2A2D35] bg-[#151821] rounded-sm p-3"
                       data-testid={`library-result-${r.chunk_id}`}>
                    <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-[#6E6B64] mb-1">
                      <span className="text-[#D4AF37]">{r.filename}</span>
                      <span>page {r.page} · score {r.score.toFixed(2)}</span>
                    </div>
                    <div className="text-sm text-[#E6E1D6] leading-relaxed whitespace-pre-wrap">
                      {highlight(r.snippet, results.query)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!results && (
              <div className="border border-dashed border-[#2A2D35] rounded-sm p-6 text-center text-sm text-[#6E6B64]">
                <BookOpen size={22} className="mx-auto opacity-40 mb-2" />
                Type at least 2 characters to search your library.<br />
                Upload as many PDFs as you like — each new one is instantly indexed and searchable.
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
